/**
 * ModuleHostPane — Briefing Room M3a (2026-06-01) + module-selection
 * dropdown (Lever 1, 2026-06-29).
 *
 * Hosts the Modules tab. Lazy-mounts the SELECTED module's SPA (Suspense +
 * ErrorBoundary + the module's own Provider) and renders a dropdown to switch
 * between the tenant's available modules. "Available" = the backend's
 * permitted-modules list (moduleStore → GET /api/modules/permitted)
 * intersected with the frontend MODULE_REGISTRY (modules the host can render).
 * While the permitted list hasn't loaded (or errored) the host degrades open
 * to the full registry so a backend hiccup never hides a working module.
 *
 * Host-chrome invariants from the 1.214.1 hotfix MUST be preserved on every
 * render path (loading + error + loaded + empty): TopBar + ResizableVoicePanel
 * + data-testid="modules-shell-root" so the view-mode switcher stays visible
 * across the TriShellLayout display:contents/none toggle. See
 * KnowledgeShell.tsx:1-15 for the precedent.
 */
import { useEffect, useMemo, useRef } from 'react';
import { ResizableVoicePanel } from '../voice/ResizableVoicePanel';
import { useUIStore } from '@/stores/uiStore';
import { useModuleStore } from '@/stores/moduleStore';
import { useSpaContextStore, type SpaContext } from '@/stores/spaContextStore';
import { MODULE_REGISTRY, REGISTRY_ENTRIES } from './moduleRegistry';
import { ModuleTile } from './ModuleTile';
import { ModulesGrid } from './ModulesGrid';
import { resolveModuleId } from './moduleActionFocus';
import type { ModuleDockEdge } from '@/stores/uiStore';

// Cross-package on-screen snapshot bus (2026-07-01). The Briefing Room lives in
// a SEPARATE package (apps/briefing-room-module) and can't import our
// spaContextStore, so it pushes its snapshot over a window CustomEvent (see that
// module's services/host/SpaContextServiceModule.ts) and we forward it here —
// mirrors the vibe:module-action / vibe:focus-panel host↔module bus. In-src
// modules (GHL / n8n) publish to spaContextStore directly instead.
const SPA_CONTEXT_EVENT = 'vibe:spa-context';
const SPA_CONTEXT_CLEAR_EVENT = 'vibe:spa-context-clear';
const BRIEFING_ROOM_MODULE_ID = 'gamemagick.briefing-room';

function NoModulesState() {
  return (
    <div
      data-testid="module-host-pane"
      data-state="empty"
      className="flex-1 flex items-center justify-center bg-vibe-bg text-vibe-text-secondary"
    >
      <div className="text-center max-w-md px-6">
        <h2 className="text-lg font-medium text-vibe-text mb-2">No modules available</h2>
        <p className="text-sm leading-relaxed">
          No loadable modules are enabled for this tenant.
        </p>
      </div>
    </div>
  );
}

export function ModuleHostPane() {
  const permittedModules = useModuleStore((s) => s.permittedModules);
  const permittedLoaded = useModuleStore((s) => s.loaded);
  const fetchPermittedModules = useModuleStore((s) => s.fetchPermittedModules);
  const activeModuleId = useUIStore((s) => s.activeModuleId);
  const openModuleIds = useUIStore((s) => s.openModuleIds);
  const dockedModule = useUIStore((s) => s.dockedModule);
  const maximizedModuleId = useUIStore((s) => s.maximizedModuleId);
  const openModule = useUIStore((s) => s.openModule);
  const closeModule = useUIStore((s) => s.closeModule);
  const focusModule = useUIStore((s) => s.focusModule);
  const dockModule = useUIStore((s) => s.dockModule);
  const toggleMaximizeModule = useUIStore((s) => s.toggleMaximizeModule);
  const viewMode = useUIStore((s) => s.viewMode);
  const setSpaContext = useSpaContextStore((s) => s.setSpaContext);

  useEffect(() => {
    void fetchPermittedModules();
  }, [fetchPermittedModules]);

  // available = the modules to offer in the dropdown. When the backend
  // permitted list has loaded, honor it (permitted ∩ registry, even if empty).
  // Otherwise degrade open to the whole registry.
  const available = useMemo<{ moduleId: string; displayName: string }[]>(() => {
    if (permittedLoaded) {
      return permittedModules
        .filter((m) => MODULE_REGISTRY[m.module_id])
        .map((m) => ({
          moduleId: m.module_id,
          displayName: m.display_name || MODULE_REGISTRY[m.module_id].displayName,
        }));
    }
    return REGISTRY_ENTRIES.map((e) => ({ moduleId: e.moduleId, displayName: e.displayName }));
  }, [permittedLoaded, permittedModules]);

  // 1.422.0 — effective OPEN tiles: persisted open ids filtered to what's
  // actually available; when nothing is open, fall back to the first
  // available module (derive-only, no setState on mount — preserves the
  // pre-tiling auto-mount behavior AND graceful stale-id fallback).
  const availableIds = useMemo(() => new Set(available.map((a) => a.moduleId)), [available]);
  const effectiveOpenIds = useMemo(() => {
    const open = openModuleIds.filter((id) => availableIds.has(id));
    if (open.length > 0) return open;
    return available[0] ? [available[0].moduleId] : [];
  }, [openModuleIds, availableIds, available]);

  const effectiveFocusedId =
    activeModuleId && effectiveOpenIds.includes(activeModuleId)
      ? activeModuleId
      : effectiveOpenIds[effectiveOpenIds.length - 1] ?? null;

  // Materialize the derive-only fallback tile before mutating the open set —
  // same rule the picker onChange + host verbs use (1.422.0 P1). Without it the
  // first real open/focus/close would act on an EMPTY store instead of the
  // auto-mounted fallback module.
  const materializeOpen = () => {
    if (openModuleIds.length === 0) effectiveOpenIds.forEach((id) => openModule(id));
  };
  const displayNameFor = (id: string) =>
    available.find((a) => a.moduleId === id)?.displayName ??
    MODULE_REGISTRY[id]?.displayName ??
    id;

  // Forward the Briefing Room's pushed on-screen snapshot to spaContextStore so
  // the host's voice/Pi agents can SEE it — but only while the Briefing Room is
  // the VISIBLE surface (Modules view active + it's the selected module). Cache
  // the last snapshot in a ref so switching AWAY and back re-applies it without
  // waiting for the module's next state change (the module keeps its own React
  // state across the display:none toggle and won't re-publish on view switch).
  const briefingVisible =
    viewMode === 'modules' && effectiveOpenIds.includes(BRIEFING_ROOM_MODULE_ID);
  const clearSpaContext = useSpaContextStore((s) => s.clearSpaContext);
  const lastBriefingSnap = useRef<SpaContext | null>(null);
  useEffect(() => {
    const onSnapshot = (e: Event) => {
      const snap = (e as CustomEvent).detail as SpaContext | undefined;
      if (!snap) return;
      // P3: force the briefing key so the forwarded snapshot lands under its
      // moduleId in the multi-publisher store regardless of package payload.
      const keyed = { ...snap, moduleId: BRIEFING_ROOM_MODULE_ID };
      lastBriefingSnap.current = keyed;
      if (briefingVisible) setSpaContext(keyed);
    };
    const onClear = () => {
      lastBriefingSnap.current = null;
      clearSpaContext(BRIEFING_ROOM_MODULE_ID);
    };
    window.addEventListener(SPA_CONTEXT_EVENT, onSnapshot);
    window.addEventListener(SPA_CONTEXT_CLEAR_EVENT, onClear);
    return () => {
      window.removeEventListener(SPA_CONTEXT_EVENT, onSnapshot);
      window.removeEventListener(SPA_CONTEXT_CLEAR_EVENT, onClear);
    };
  }, [briefingVisible, setSpaContext, clearSpaContext]);
  // Activate-republish: when the Briefing Room becomes the visible surface,
  // re-apply its last-known snapshot; when its tile closes, drop its keyed
  // entry so a closed tile never lingers in the agents' [On screen now] block.
  useEffect(() => {
    if (briefingVisible && lastBriefingSnap.current) setSpaContext(lastBriefingSnap.current);
    if (!briefingVisible) clearSpaContext(BRIEFING_ROOM_MODULE_ID);
  }, [briefingVisible, setSpaContext, clearSpaContext]);

  // P3 host verbs — let voice/Pi drive the TILING itself over the same
  // 'vibe:module-action' bus the per-module verbs use: open_module,
  // close_module, focus_module, dock_module {edge}, maximize_module. The id
  // param accepts an exact module id or a display name (resolveModuleId).
  // Kept in refs so the listener stays stable across renders.
  const hostVerbState = useRef({
    available,
    effectiveOpenIds,
    effectiveFocusedId,
    openModuleIdsLen: openModuleIds.length,
  });
  hostVerbState.current = {
    available,
    effectiveOpenIds,
    effectiveFocusedId,
    openModuleIdsLen: openModuleIds.length,
  };
  useEffect(() => {
    const onHostVerb = (e: Event) => {
      const d = (e as CustomEvent).detail as
        | { action?: string; params?: Record<string, unknown>; module_id?: string }
        | undefined;
      const action = d?.action;
      if (
        action !== 'open_module' &&
        action !== 'close_module' &&
        action !== 'focus_module' &&
        action !== 'dock_module' &&
        action !== 'maximize_module'
      ) {
        return;
      }
      const { available: avail, effectiveOpenIds: openIds, effectiveFocusedId: focusedId, openModuleIdsLen } =
        hostVerbState.current;
      const params = d?.params ?? {};
      const rawId = params.module_id ?? params.id ?? d?.module_id;
      const resolved = resolveModuleId(rawId, avail);
      // Materialize the derive-only fallback tile before mutating the open
      // set (same rule as the picker onChange — see 1.422.0 P1 RED test).
      const materialize = () => {
        if (openModuleIdsLen === 0) openIds.forEach((id) => openModule(id));
      };
      if (action === 'open_module' || action === 'focus_module') {
        if (resolved) {
          materialize();
          openModule(resolved);
        }
        return;
      }
      const target = resolved ?? focusedId;
      if (!target) return;
      if (action === 'close_module') {
        materialize();
        closeModule(target);
      } else if (action === 'maximize_module') {
        materialize();
        toggleMaximizeModule(target);
      } else if (action === 'dock_module') {
        const edge = params.edge;
        materialize();
        if (edge === 'left' || edge === 'right' || edge === 'top' || edge === 'bottom') {
          dockModule(target, edge as ModuleDockEdge);
        } else if (edge === null || edge === 'none' || edge === undefined) {
          dockModule(target, null);
        }
      }
    };
    window.addEventListener('vibe:module-action', onHostVerb);
    return () => window.removeEventListener('vibe:module-action', onHostVerb);
  }, [openModule, closeModule, dockModule, toggleMaximizeModule]);

  return (
    <div
      data-testid="modules-shell-root"
      className="flex flex-col h-full w-full overflow-hidden bg-vibe-bg text-vibe-text"
    >
      {/* TopBar hoisted to App level (2026-07-03) — voice strip stays here. */}
      <ResizableVoicePanel />

      {available.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-vibe-border bg-vibe-bg-secondary">
          <label htmlFor="module-select" className="text-xs text-vibe-text-secondary">
            Module
          </label>
          {/* 1.422.0 — the dropdown is now an OPEN-module picker: selecting a
              module opens it as a tile (or focuses its existing tile — max 1
              instance per module). Which modules are OPEN is shown in the pill
              bar below (native <option> labels stay clean for accessible-name
              stability). */}
          <select
            id="module-select"
            data-testid="module-select"
            value={effectiveFocusedId ?? ''}
            onChange={(e) => {
              // Materialize the derive-only fallback tile before opening the
              // picked module — otherwise the first real open would REPLACE
              // the auto-mounted module instead of tiling next to it.
              if (openModuleIds.length === 0) {
                effectiveOpenIds.forEach((id) => openModule(id));
              }
              openModule(e.target.value);
            }}
            className="text-sm bg-vibe-bg text-vibe-text border border-vibe-border rounded px-2 py-1 outline-none focus:ring-1 focus:ring-vibe-accent"
          >
            {available.map((m) => (
              <option key={m.moduleId} value={m.moduleId}>
                {m.displayName}
              </option>
            ))}
          </select>
          <span className="text-xs text-vibe-text-secondary">
            {effectiveOpenIds.length > 1
              ? `${effectiveOpenIds.length} modules open`
              : 'pick another to open it side-by-side'}
          </span>
        </div>
      )}

      {/* Open-module pill bar (2026-07-31) — the always-visible identifier for
          which modules are open + focused + maximized, and the reliable
          host-level way to un-maximize. It lives ABOVE the grid so it stays
          visible even when ModulesGrid renders a maximized tile solo (whose own
          restore control is a thin, occludable tile-header button). */}
      {effectiveOpenIds.length > 0 && (
        <div
          data-testid="module-pill-bar"
          className="flex items-center gap-1.5 overflow-x-auto border-b border-vibe-border bg-vibe-bg-secondary px-4 py-1.5"
        >
          {effectiveOpenIds.map((id) => {
            const name = displayNameFor(id);
            const isFocused = effectiveFocusedId === id;
            const isMax = maximizedModuleId === id;
            return (
              <span
                key={id}
                data-testid={`module-pill-${id}`}
                data-active={isFocused ? 'true' : 'false'}
                data-maximized={isMax ? 'true' : 'false'}
                className={`inline-flex shrink-0 items-center gap-1 rounded-full border py-0.5 text-xs ${
                  isFocused
                    ? 'border-vibe-accent/60 bg-vibe-bg-tertiary text-vibe-text'
                    : 'border-vibe-border text-vibe-text-secondary hover:bg-vibe-bg-tertiary hover:text-vibe-text'
                }`}
              >
                <button
                  type="button"
                  data-testid={`module-pill-focus-${id}`}
                  title={isFocused ? `${name} (focused)` : `Focus ${name}`}
                  onClick={() => {
                    materializeOpen();
                    focusModule(id);
                  }}
                  className="inline-flex items-center gap-1 pl-2 outline-none"
                >
                  <span
                    aria-hidden="true"
                    className={`h-1.5 w-1.5 rounded-full ${
                      isFocused ? 'bg-vibe-accent' : 'bg-vibe-text-tertiary'
                    }`}
                  />
                  <span className="max-w-[10rem] truncate">{name}</span>
                  {isMax && (
                    <span aria-hidden="true" className="text-vibe-text-secondary">
                      ⤢
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  data-testid={`module-pill-close-${id}`}
                  title={`Close ${name}`}
                  onClick={() => {
                    materializeOpen();
                    closeModule(id);
                  }}
                  className="rounded px-1.5 text-vibe-text-secondary hover:text-vibe-text"
                >
                  ✕
                </button>
              </span>
            );
          })}
          {maximizedModuleId && effectiveOpenIds.includes(maximizedModuleId) && (
            <button
              type="button"
              data-testid="module-restore-btn"
              title="Restore the maximized module"
              onClick={() => toggleMaximizeModule(maximizedModuleId)}
              className="ml-auto shrink-0 rounded border border-vibe-border px-2 py-0.5 text-xs text-vibe-text-secondary hover:bg-vibe-bg-tertiary hover:text-vibe-text"
            >
              ⤢ Restore
            </button>
          )}
        </div>
      )}

      {effectiveOpenIds.length > 0 ? (
        <div
          data-testid="module-host-pane"
          data-state="loaded"
          className="flex-1 overflow-hidden"
        >
          <ModulesGrid
            docked={
              dockedModule && effectiveOpenIds.includes(dockedModule.id) ? dockedModule : null
            }
            maximizedModuleId={
              maximizedModuleId && effectiveOpenIds.includes(maximizedModuleId)
                ? maximizedModuleId
                : null
            }
            tiles={effectiveOpenIds.map((moduleId) => ({
              moduleId,
              node: (
                <ModuleTile
                  moduleId={moduleId}
                  displayName={
                    available.find((a) => a.moduleId === moduleId)?.displayName ??
                    MODULE_REGISTRY[moduleId]?.displayName ??
                    moduleId
                  }
                  focused={effectiveFocusedId === moduleId}
                  docked={dockedModule?.id === moduleId ? dockedModule.edge : null}
                  maximized={maximizedModuleId === moduleId}
                  onFocus={() => focusModule(moduleId)}
                  onClose={() => closeModule(moduleId)}
                  onDock={(edge) => dockModule(moduleId, edge)}
                  onToggleMaximize={() => toggleMaximizeModule(moduleId)}
                />
              ),
            }))}
          />
        </div>
      ) : (
        <NoModulesState />
      )}
    </div>
  );
}
